import { CHART_COLORS, CHART_TYPES, SCALE_RATIO_THRESHOLD } from './const';
import type { ChartObject, ChartTrendSeries, CompareChartGraphDataItem, ChartCompany } from './types';

const createChartService = () => {
  const isChartObject = (obj: unknown): obj is ChartObject => {
    const chartObj = obj as Partial<ChartObject>;
    if (
      typeof obj !== 'object' ||
      obj === null ||
      typeof chartObj.chart?.type !== 'string' ||
      !Array.isArray(chartObj.series) ||
      typeof chartObj.title?.text !== 'string' ||
      !Array.isArray(chartObj.xAxis?.categories)
    ) {
      return false;
    }

    const seriesValid = chartObj.series.every(
      (s) =>
        typeof s.name === 'string' &&
        Array.isArray(s.data) &&
        s.data.every((d: { y: number; [key: string]: unknown }) => typeof d.y === 'number' || d.y === null)
    );

    return seriesValid;
  };

  const normalize = (str: string) =>
    str
      .toLowerCase()
      .replace(/\s*&\s*/g, ' & ') // normalize spacing around &
      .replace(/&/g, 'and') // treat & as 'and'
      .replace(/[^a-z0-9\s]/g, '') // remove all other non-alphanumeric chars
      .replace(/\s+/g, ' ') // collapse multiple spaces
      .trim();

  const matchesDomainMeta = (value: string | undefined, meta?: ChartCompany): boolean => {
    if (!value || !meta) {
      return false;
    }

    const { domain, companyName } = meta;
    const normalizedValue = normalize(value);
    const normalizedDomain = domain ? normalize(domain) : '';
    const normalizedCompanyName = companyName ? normalize(companyName) : '';

    return (
      normalizedValue === normalizedDomain ||
      normalizedValue === normalizedCompanyName ||
      normalizedCompanyName.includes(normalizedValue) ||
      normalizedDomain.includes(normalizedValue)
    );
  };

  const findDomainMeta = (domains: ChartCompany[] | undefined, value: string | undefined): ChartCompany | undefined => {
    return domains?.find((d) => matchesDomainMeta(value, d));
  };

  const isValidProspectIndex = (index: number | undefined, length: number): boolean =>
    typeof index === 'number' && index >= 0 && index < length;

  const isCompareChart = (chart: ChartObject): boolean =>
    chart.series.length === 1 &&
    chart?.xAxis?.categories?.length > 0 &&
    !!chart.xargs?.domains &&
    chart.xargs?.domains.length > 0 &&
    chart.xAxis.categories.some((cat) => chart.xargs?.domains?.some((d) => matchesDomainMeta(cat, d)));

  const transformToCompareChart = (chart: ChartObject): CompareChartGraphDataItem[] => {
    const series = chart?.series?.[0];
    const rawSeriesData = series?.data || [];
    const data = rawSeriesData
      .map((c, i) => {
        const xAxisValue = chart?.xAxis?.categories?.[i];
        const xAxisDomainData = findDomainMeta(chart?.xargs?.domains, xAxisValue);
        const prospectIndex = series?.prospectIndex;
        const hasValidProspectIndex = isValidProspectIndex(prospectIndex, rawSeriesData.length);
        const isHighlighted = hasValidProspectIndex ? i === prospectIndex : false;

        const companyName = xAxisDomainData?.companyName || xAxisValue;
        return {
          name: companyName || xAxisValue,
          favicon: xAxisDomainData?.favicon,
          value: Math.round(c.y * 100) / 100,
          isHighlighted,
        };
      })
      .filter((item) => !!item.value)
      .filter(Boolean) as CompareChartGraphDataItem[];
    return data.sort((a, b) => b.value - a.value);
  };

  const toNums = (s: { data: Array<{ y: number | null | undefined }> }) => s.data.map((p) => Number(p?.y) || 0);

  const seriesMaxAbs = (arr: number[]) => (arr.length ? Math.max(...arr.map((v) => Math.abs(v))) : 0);

  const getScaleRatio = (maxes: number[]) => {
    const nonZeroMaxes = maxes.filter((v) => v > 0);
    if (nonZeroMaxes.length < 2) {
      return 1;
    }
    return Math.max(...nonZeroMaxes) / Math.min(...nonZeroMaxes);
  };

  const hasMixedFormat = (chart: ChartObject): boolean => {
    const formats = Array.from(new Set((chart.series ?? []).map((s) => s?.dataFormat ?? 'number')));
    const isMixedByFormat = formats.length > 1;

    const maxes = (chart.series ?? []).map((s) => seriesMaxAbs(toNums(s)));
    const isMixedByScale = getScaleRatio(maxes) >= SCALE_RATIO_THRESHOLD;

    return isMixedByFormat || isMixedByScale;
  };

  const transformToHighchartsSeries = (chart: ChartObject): ChartTrendSeries[] => {
    const isMixed = hasMixedFormat(chart);
    const isBarLike = chart.chart.type === 'bar' || chart.chart.type === 'column';
    const maxes = (chart.series ?? []).map((s) => seriesMaxAbs(toNums(s)));
    const primaryIndex = isMixed ? maxes.indexOf(Math.max(...maxes)) : 0;
    const chartType = chart.chart.type;

    return chart.series.map((item, seriesIndex) => {
      const seriesData: number[] = item.data.map((point) => point.y || 0);
      const xAxisDomainData = chart?.xargs?.domains?.find((d) => d.domain === item.domain);
      const isPrimary = seriesIndex === primaryIndex;

      const base = {
        type: chartType === 'bar' ? CHART_TYPES.COLUMN : chartType === 'line' ? CHART_TYPES.LINE : chartType,
        name: xAxisDomainData?.companyName || item.name,
        data: seriesData,
        color: CHART_COLORS[seriesIndex % CHART_COLORS.length],
        id: `series ${seriesIndex}`,
        visible: true,
      } as ChartTrendSeries;

      if (isMixed && isBarLike) {
        return {
          ...base,
          yAxis: isPrimary ? 0 : 1,
          stacking: undefined,
        };
      }
      return base;
    });
  };

  return {
    isChartObject,
    isCompareChart,
    hasMixedFormat,
    transformToCompareChart,
    transformToHighchartsSeries,
  };
};

const ChartService = createChartService();

export default ChartService;
